import { hash } from '@ember/helper';
import { action } from '@ember/object';
import Component from '@glimmer/component';

export interface UiPaginationSignature {
  Args: {
    pageCount: number;
    pageNumber: number;
    pageSize: number;
    recordCount: number;
    setPageNumber: (pageNumber: number) => void;
  };
  Blocks: {
    default: [
      {
        goToPreviousPage: () => void;
        goToNextPage: () => void;
        hasPreviousPage: boolean;
        isLastPage: boolean;
        start: number;
        end: number;
        totalCount: number;
      },
    ];
  };
}

export default class UiPagination extends Component<UiPaginationSignature> {
  get hasPreviousPage(): boolean {
    return this.args.pageNumber > 1;
  }

  get end(): number {
    if (this.isLastPage) {
      return this.start + this.lastPageSize - 1;
    } else {
      return this.start + this.args.pageSize - 1;
    }
  }

  get isLastPage(): boolean {
    return this.args.pageCount === 0 || this.args.pageNumber === this.args.pageCount;
  }

  get hasNextPage(): boolean {
    return this.args.pageNumber < this.args.pageCount;
  }

  get lastPageSize(): number {
    const recordCount = this.args.recordCount;
    const pageSize = this.args.pageSize;
    const remainder = recordCount % pageSize;

    if (remainder === 0) {
      return pageSize;
    } else {
      return remainder;
    }
  }

  get nextPageNumber(): number {
    return this.args.pageNumber + 1;
  }

  get previousPageNumber(): number {
    const previousPageNumber = this.args.pageNumber - 1;

    if (previousPageNumber > 0) {
      return previousPageNumber;
    } else {
      return 1;
    }
  }

  get start(): number {
    const pageNumber = this.args.pageNumber;
    const pageSize = this.args.pageSize;

    return pageNumber * pageSize - pageSize + 1;
  }

  @action
  goToNextPage(): void {
    const hasNextPage = this.hasNextPage;
    const nextPageNumber = this.nextPageNumber;

    if (hasNextPage) {
      this.args.setPageNumber(nextPageNumber);
    }
  }

  @action
  goToPreviousPage(): void {
    const hasPreviousPage = this.hasPreviousPage;
    const previousPageNumber = this.previousPageNumber;

    if (hasPreviousPage) {
      this.args.setPageNumber(previousPageNumber);
    }
  }

  <template>
    {{yield
      (hash
        goToPreviousPage=this.goToPreviousPage
        goToNextPage=this.goToNextPage
        hasPreviousPage=this.hasPreviousPage
        isLastPage=this.isLastPage
        start=this.start
        end=this.end
        totalCount=@recordCount
      )
    }}
  </template>
}
